import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { SEO } from "@/lib/seo";
import { CLINIC_PHONE, CLINIC_FAX } from "@/lib/mailto";
import { FileText, Timer, ClipboardCheck, ShieldCheck, Phone, Printer, Info, Mail } from "lucide-react";

const STEP_ICONS = [FileText, Printer, ClipboardCheck, Timer, ShieldCheck];

export default function ReferralsPage() {
    const { t } = useTranslation();
    const steps = [1, 2, 3, 4, 5].map((n, i) => ({
        icon: STEP_ICONS[i],
        title: t(`referralsPage.step${n}Title`),
        body: t(`referralsPage.step${n}Body`),
    }));
    return (
        <>
            <SEO title={t("referralsPage.seoTitle")} description={t("referralsPage.seoDescription")} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ label: t("referralsPage.crumb") }]} />
            </div>
            <section className="container-page pt-6 pb-4">
                <div className="max-w-3xl">
                    <span className="chip-teal">{t("referralsPage.chip")}</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                        {t("referralsPage.title")}
                    </h1>
                    <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed text-lg">
                        {t("referralsPage.intro")}
                    </p>
                </div>
            </section>

            <section className="container-page pb-4">
                <div
                    className="card-soft p-6 sm:p-7 grain"
                    data-testid="referrals-family-physician-notice"
                    style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.10) 0%, rgba(255,255,255,1) 60%)" }}
                >
                    <div className="flex items-start gap-4">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[color:var(--brand-teal-2)] border border-[color:var(--brand-teal-2)]/25 shrink-0">
                            <Info className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-wider text-[color:var(--brand-teal-2)] font-semibold">
                                {t("referralsPage.important")}
                            </p>
                            <h2 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-[color:var(--brand-navy)]">
                                {t("referralsPage.familyOnlyTitle")}
                            </h2>
                            <p className="mt-2 text-[color:var(--text-muted)] leading-relaxed">
                                <Trans i18nKey="referralsPage.familyOnlyBody" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} />
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-page pb-4">
                <div
                    className="card-soft p-5 sm:p-6 flex items-start gap-3"
                    data-testid="referrals-patient-email-notice"
                    style={{ background: "linear-gradient(135deg, rgba(180,83,9,0.08) 0%, rgba(255,255,255,1) 65%)" }}
                >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-[color:var(--warning)] border border-[color:var(--warning)]/30 shrink-0">
                        <Mail className="h-4 w-4" />
                    </span>
                    <div>
                        <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--warning)" }}>
                            {t("referralsPage.pleaseInclude")}
                        </p>
                        <h3 className="mt-1 font-display text-lg sm:text-xl font-semibold text-[color:var(--brand-navy)]">
                            {t("referralsPage.patientEmailTitle")}
                        </h3>
                        <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                            <Trans i18nKey="referralsPage.patientEmailBody" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} />
                        </p>
                        <p className="mt-2 text-xs text-[color:var(--text-soft)]">
                            {t("referralsPage.patientEmailNote")}
                        </p>
                    </div>
                </div>
            </section>

            <section className="container-page pb-4">
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
                    {steps.map((s, i) => (
                        <div key={s.title} className="card-soft p-6">
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)] font-semibold">
                                {i + 1}
                            </div>
                            <h3 className="mt-3 font-display text-lg font-semibold text-[color:var(--brand-navy)]">{s.title}</h3>
                            <p className="mt-1.5 text-sm text-[color:var(--text-muted)] leading-relaxed">{s.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="container-page pb-10">
                <div className="grid gap-6 lg:grid-cols-2 items-stretch">
                    <div className="card-soft p-6 sm:p-7 h-full flex flex-col">
                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                            <Timer className="h-5 w-5" />
                        </div>
                        <h2 className="mt-4 font-display text-2xl font-semibold text-[color:var(--brand-navy)]">
                            {t("referralsPage.waitTimesTitle")}
                        </h2>
                        <ul className="mt-3 space-y-2 text-sm text-[color:var(--text-muted)] leading-relaxed pl-5 list-disc marker:text-[color:var(--brand-teal)]">
                            <li>{t("referralsPage.wait1")}</li>
                            <li>{t("referralsPage.wait2")}</li>
                            <li>
                                <Trans i18nKey="referralsPage.wait3" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} />
                            </li>
                        </ul>
                        <p className="mt-auto pt-4 text-xs text-[color:var(--text-soft)]">
                            {t("referralsPage.waitFooter")}
                        </p>
                    </div>
                    <div
                        className="card-soft p-6 sm:p-7 h-full flex flex-col grain"
                        data-testid="referral-contact-card"
                        style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.10) 0%, rgba(255,255,255,1) 55%)" }}
                    >
                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                            <Printer className="h-5 w-5" />
                        </div>
                        <h2 className="mt-4 font-display text-2xl font-semibold text-[color:var(--brand-navy)]">
                            {t("referralsPage.howToSendTitle")}
                        </h2>
                        <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                            <Trans i18nKey="referralsPage.howToSendBody" components={{ 1: <strong /> }} />
                        </p>
                        <div className="mt-5 grid gap-2 sm:grid-cols-2">
                            <a
                                href={`fax:${CLINIC_FAX.replace(/[^\d+]/g, "")}`}
                                className="flex items-center justify-center gap-1.5 rounded-xl bg-[color:var(--brand-navy)] px-4 py-3 text-sm font-semibold text-white hover:bg-[color:var(--brand-navy-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-teal-2)]"
                                data-testid="referral-fax-cta"
                            >
                                <Printer className="h-4 w-4" />
                                {t("referralsPage.faxLabel")} · {CLINIC_FAX}
                            </a>
                            <a
                                href={`tel:${CLINIC_PHONE.replace(/[^\d+]/g, "")}`}
                                className="flex items-center justify-center gap-1.5 rounded-xl border border-[color:var(--border-default)] bg-white px-4 py-3 text-sm font-semibold text-[color:var(--brand-navy)] hover:bg-[color:var(--bg-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-teal-2)]"
                                data-testid="referral-phone-cta"
                            >
                                <Phone className="h-4 w-4" />
                                {t("referralsPage.officeLabel")} · {CLINIC_PHONE}
                            </a>
                        </div>
                        <p className="mt-auto pt-5 rounded-xl border border-[color:var(--border-default)] bg-white/60 p-3 text-xs text-[color:var(--text-muted)] leading-relaxed">
                            {t("referralsPage.privacyNote")}
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
